import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { normalizeProfileId, normalizeUserRole } from '@/lib/fetchRoleBasedProjects';
import {
  fetchSidebarProjectsLite,
  getCachedSidebarProjects,
  mergeSidebarProjectsCache,
  type SidebarProjectRow,
} from '@/lib/sidebarProjectsCache';

export interface SidebarProject {
  id: string;
  project_name: string;
}

function inferRoleFromPath(pathname: string): string | null {
  if (pathname.startsWith('/management')) return 'management';
  if (pathname.startsWith('/subcontractor')) return 'subcontractor';
  if (pathname.startsWith('/client')) return 'client';
  return null;
}

export function useSidebarProjects(
  profileId: string | undefined,
  role: string | undefined,
  enabled = true,
  options?: {
    profileLoading?: boolean;
    fallbackProject?: SidebarProjectRow | null;
  },
) {
  const location = useLocation();
  const { user, profile, loading: authLoading } = useAuth();
  const safeProfileId =
    normalizeProfileId(profileId) ??
    normalizeProfileId(profile?.id) ??
    normalizeProfileId(user?.id);
  const safeRole =
    normalizeUserRole(role) ??
    normalizeUserRole(profile?.user_type) ??
    inferRoleFromPath(location.pathname);
  const profileLoading = options?.profileLoading ?? authLoading;
  const fallbackProject = options?.fallbackProject ?? null;

  const [projects, setProjects] = useState<SidebarProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasResolved, setHasResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const genRef = useRef(0);

  const applyFallbackProject = useCallback(
    (rows: SidebarProject[]): SidebarProject[] => {
      if (!fallbackProject?.id || !safeProfileId || !safeRole) return rows;
      const merged = mergeSidebarProjectsCache(safeProfileId, safeRole, [fallbackProject]);
      if (merged.length === 0) return rows;
      const byId = new Map<string, SidebarProject>();
      for (const row of [...rows, ...merged]) byId.set(row.id, row);
      return Array.from(byId.values());
    },
    [fallbackProject, safeProfileId, safeRole],
  );

  const retry = useCallback(() => {
    if (!enabled || profileLoading || !safeProfileId || !safeRole) return;

    const gen = ++genRef.current;
    let cancelled = false;
    setLoading(true);
    setHasResolved(false);
    setError(null);

    void fetchSidebarProjectsLite(safeProfileId, safeRole, { forceRefresh: true })
      .then(({ projects: rows, error: fetchError }) => {
        if (cancelled || genRef.current !== gen) return;
        const cached = getCachedSidebarProjects(safeProfileId, safeRole);
        const seeded = applyFallbackProject(cached ?? []);
        const next = applyFallbackProject(Array.isArray(rows) ? rows : []);
        setProjects((prev) => {
          if (next.length > 0) return next;
          return prev.length > 0 ? prev : seeded;
        });
        setError(next.length > 0 ? null : fetchError);
        setHasResolved(true);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || genRef.current !== gen) return;
        setError(err instanceof Error ? err.message : 'Failed to load projects');
        setHasResolved(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, profileLoading, safeProfileId, safeRole, applyFallbackProject]);

  useEffect(() => {
    if (!enabled) {
      setProjects([]);
      setLoading(false);
      setHasResolved(true);
      setError(null);
      return;
    }

    if (profileLoading || !safeProfileId || !safeRole) {
      setLoading(true);
      setHasResolved(false);
      return;
    }

    const gen = ++genRef.current;
    let cancelled = false;

    const cached = getCachedSidebarProjects(safeProfileId, safeRole);
    const seeded = applyFallbackProject(cached ?? []);
    if (seeded.length > 0) {
      setProjects(seeded);
    }
    setLoading(true);
    setHasResolved(false);
    setError(null);

    void fetchSidebarProjectsLite(safeProfileId, safeRole)
      .then(({ projects: rows, error: fetchError }) => {
        if (cancelled || genRef.current !== gen) return;
        const next = applyFallbackProject(Array.isArray(rows) ? rows : []);
        setProjects((prev) => {
          if (next.length > 0) return next;
          return prev.length > 0 ? prev : seeded;
        });
        console.log('[useSidebarProjects] sidebar project count', {
          profileId: safeProfileId,
          role: safeRole,
          table: 'jphph_projects',
          count: next.length,
          fetchError,
          path: location.pathname,
        });
        setError(next.length > 0 ? null : fetchError);
        setHasResolved(true);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || genRef.current !== gen) return;
        const message = err instanceof Error ? err.message : 'Failed to load projects';
        console.error('[useSidebarProjects] load failed', {
          error: err,
          profileId: safeProfileId,
          role: safeRole,
          table: 'jphph_projects',
        });
        setError(message);
        setProjects((prev) => (prev.length > 0 ? prev : seeded));
        setHasResolved(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    profileLoading,
    safeProfileId,
    safeRole,
    applyFallbackProject,
    location.pathname,
    fallbackProject?.id,
  ]);

  return { projects, loading, hasResolved, error, retry };
}
